/**
 * Quotation roll-up totals, derived rather than stored (#78).
 *
 * `quotations.total_price` and `quotations.total_cost` are written as 0 when a
 * quotation is created and nothing ever writes them again. Every screen that
 * read those columns — the quotations list's "מחיר סופי", the dashboard's
 * revenue tiles, the project detail grid — therefore showed ₪0.00 for every
 * quotation ever made, regardless of status.
 *
 * The fix is to stop reading the columns rather than to start maintaining
 * them. Keeping a stored roll-up correct would mean writing it back from all
 * eight mutation paths (item add/edit/delete, system add/edit/delete,
 * parameter change, discount change) and every one of them missing it
 * silently reproduces this exact bug. Deriving has one code path and cannot
 * drift, and it is what `.claude/rules/global-rules.md` already asks for:
 * "customer prices calculated as cost + markup (never stored directly)".
 *
 * The list query already loads `quotation_systems → quotation_items →
 * component`, which is everything `calculateQuotationTotals` needs, so this
 * costs no extra round trip.
 */

import type { DbQuotation } from '../types';
import { convertDbQuotationToQuotationProject } from '../lib/utils';
import { calculateQuotationTotals } from './quotationCalculations';
import { logger } from '../lib/logger';

/**
 * Recompute one quotation's `total_price` / `total_cost` from its nested
 * systems and items.
 *
 * `total_price` is `netQuoteILS` — markup, risk and discount applied, VAT not.
 * That is deliberately **not** `finalTotalILS`, despite the column being headed
 * "מחיר סופי": the quotation editor displays exactly one total, the bold
 * "סה"כ לפני מע"מ" line in QuotationSummary, and it is the pre-VAT figure. A
 * list showing 17% more than the quotation it links to is a number the reader
 * cannot place, which is the complaint that produced this change. The list and
 * the quotation must agree before either matches its own header.
 *
 * `total_cost` is the un-marked-up line cost, matching the column's meaning
 * everywhere else in the schema.
 *
 * A quotation whose systems were not loaded (a bare row from a `select('id')`
 * or a partial fetch) has nothing to roll up, so it is returned untouched
 * rather than being rewritten to 0 — overwriting a real stored figure with a
 * derived zero would be worse than the bug this fixes.
 */
export function withDerivedTotals(quotation: DbQuotation): DbQuotation {
  if (!quotation.quotation_systems) return quotation;

  try {
    const project = convertDbQuotationToQuotationProject(quotation);
    const calc = calculateQuotationTotals(project);

    return {
      ...quotation,
      total_price: calc.netQuoteILS,
      total_cost: calc.totalCostILS,
    };
  } catch (err) {
    // One malformed quotation must not blank the whole list. Fall back to the
    // stored columns and say so — a silent catch here would look exactly like
    // the bug we are fixing.
    logger.error('Failed to derive totals for quotation', {
      id: quotation.id,
      error: err,
    });
    return quotation;
  }
}

/** `withDerivedTotals` over a list. */
export function withDerivedTotalsAll(quotations: DbQuotation[]): DbQuotation[] {
  return quotations.map(withDerivedTotals);
}
